import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import { useCanal } from '../lib/tiempoReal'
import { clasesDeColor } from '../lib/estados'
import type { Oportunidad, PaseDeOportunidad } from '../lib/tipos'

const cuando = (valor: string | null): string =>
  valor === null
    ? '—'
    : new Date(valor.replace(' ', 'T') + 'Z').toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })

/** Lo que queda antes de que salte al siguiente del departamento. */
const leQueda = (expira: string | null): string | null => {
  if (expira === null) return null

  const horas = Math.floor((new Date(expira.replace(' ', 'T') + 'Z').getTime() - Date.now()) / 3_600_000)

  if (horas < 0) return 'pasa al siguiente'
  if (horas < 1) return 'queda menos de 1 h'

  return `quedan ${horas} h`
}

/**
 * Las oportunidades del departamento.
 *
 * Es la idea nueva del 15 de octubre: lo que el comercial ya no ve —un rechazado,
 * un posible cambio, una baja, un CUPS de más de 15.000 kWh— no se pierde, le
 * llega a quien tenga el permiso. Y llega tapado: «que no tenga ni el teléfono ni
 * el CUPS, para que tenga que usar la oportunidad y así se pueda rastrear quién
 * la ha usado».
 *
 * Duran 48 horas por persona. Si nadie la abre, el temporizador la pasa al
 * siguiente, y en el pie de cada una queda por cuántas manos ha ido.
 */
export function Oportunidades({ ir }: { ir: (destino: string) => void }) {
  const clienteQuery = useQueryClient()
  const [aviso, setAviso] = useState<string | null>(null)
  const [verCerradas, setVerCerradas] = useState(false)
  const [abierta, setAbierta] = useState<number | null>(null)

  const lista = useQuery({
    queryKey: ['oportunidades', verCerradas],
    queryFn: () =>
      api<{ oportunidades: Oportunidad[]; horas: number }>(
        `/api/oportunidades?cerradas=${verCerradas ? '1' : '0'}`,
      ),
  })

  useCanal('sala', () => void clienteQuery.invalidateQueries({ queryKey: ['oportunidades'] }))

  const usar = useMutation({
    mutationFn: (id: number) => api(`/api/oportunidades/${id}/usar`, { metodo: 'POST' }),
    onSuccess: () => {
      setAviso(null)
      void clienteQuery.invalidateQueries({ queryKey: ['oportunidades'] })
    },
    onError: (e) => setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido abrir'),
  })

  const cerrar = useMutation({
    mutationFn: (id: number) => api(`/api/oportunidades/${id}/cerrar`, { metodo: 'POST' }),
    onSuccess: () => void clienteQuery.invalidateQueries({ queryKey: ['oportunidades'] }),
    onError: (e) => setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido cerrar'),
  })

  const oportunidades = lista.data?.oportunidades ?? []
  const mias = oportunidades.filter((una) => una.asignada_es_mia && una.cerrada_en === null)

  return (
    <div>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold">Oportunidades</h1>
          <p className="text-xs text-slate-500">
            Tienes {mias.length} asignada{mias.length === 1 ? '' : 's'}. Cada una te dura{' '}
            {lista.data?.horas ?? 48} horas laborables; luego pasa a otra persona del departamento.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-500">
          <input
            type="checkbox"
            checked={verCerradas}
            onChange={(e) => setVerCerradas(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 accent-marca-700"
          />
          Ver también las cerradas
        </label>
      </header>

      {aviso && (
        <p className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {aviso}
        </p>
      )}

      {lista.isLoading && <p className="text-sm text-slate-400">Cargando…</p>}
      {oportunidades.length === 0 && !lista.isLoading && (
        <p className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-400 dark:border-slate-800 dark:bg-slate-900">
          Nada pendiente. Las oportunidades aparecen solas cuando un contrato cae en un estado que
          lleva tu departamento.
        </p>
      )}

      <ul className="grid gap-3">
        {oportunidades.map((una) => (
          <li
            key={una.id}
            className={`rounded-xl border bg-white p-4 dark:bg-slate-900 ${
              una.cerrada_en !== null
                ? 'border-slate-200 opacity-60 dark:border-slate-800'
                : una.asignada_es_mia
                  ? 'border-marca-500'
                  : 'border-slate-200 dark:border-slate-800'
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {una.etiqueta}
              </span>
              <span className="font-mono text-sm font-semibold">{una.codigo}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${clasesDeColor(una.estado_color)}`}>
                {una.estado_etiqueta}
              </span>
              {una.compania && <span className="text-xs text-slate-500">{una.compania}</span>}

              <span className="ml-auto flex flex-wrap items-center gap-2">
                {una.cerrada_en !== null ? (
                  <span className="text-xs text-slate-400">cerrada el {cuando(una.cerrada_en)}</span>
                ) : una.usada_por === null ? (
                  <>
                    {una.expira_en !== null && (
                      <span className="text-xs font-medium text-amber-700">{leQueda(una.expira_en)}</span>
                    )}
                    <button
                      onClick={() => usar.mutate(una.id)}
                      disabled={usar.isPending}
                      className="rounded-lg bg-marca-700 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-marca-800 disabled:opacity-50"
                    >
                      Usar la oportunidad
                    </button>
                  </>
                ) : (
                  <>
                    {una.ficha_uuid !== null && (
                      <button
                        onClick={() => ir(`/fichas/${una.ficha_uuid}`)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium transition hover:border-marca-500 dark:border-slate-700"
                      >
                        Abrir el contrato
                      </button>
                    )}
                    <button
                      onClick={() => cerrar.mutate(una.id)}
                      disabled={cerrar.isPending}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium transition hover:border-red-300 hover:text-red-700 disabled:opacity-50 dark:border-slate-700"
                    >
                      Cerrarla
                    </button>
                  </>
                )}
              </span>
            </div>

            {una.motivo && <p className="mt-2 text-sm">{una.motivo}</p>}

            {una.usada_por === null ? (
              <p className="mt-2 text-xs text-slate-400">
                Los datos del cliente aparecen al usarla. Queda anotado que la has abierto tú.
              </p>
            ) : (
              <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs">
                {una.cliente && <Par etiqueta="Cliente" valor={una.cliente} />}
                {una.telefono_origen && <Par etiqueta="Teléfono" valor={una.telefono_origen} />}
                {una.cups && <Par etiqueta="CUPS" valor={una.cups} mono />}
                {una.puntos_computados !== null && (
                  <Par etiqueta="Puntos" valor={String(una.puntos_computados)} />
                )}
              </dl>
            )}

            <p className="mt-2 border-t border-slate-100 pt-2 text-xs text-slate-400 dark:border-slate-800">
              {una.origen && <>La originó {una.origen}. </>}
              {una.usada_por !== null ? (
                <>Abierta por {una.usada_por} el {cuando(una.usada_en)}.</>
              ) : una.asignada_a !== null ? (
                <>Le toca a {una.asignada_es_mia ? 'ti' : una.asignada_a}.</>
              ) : (
                <>Sin asignar: nadie tiene todavía el permiso de este departamento.</>
              )}{' '}
              <button
                onClick={() => setAbierta(abierta === una.id ? null : una.id)}
                className="font-medium underline decoration-dotted hover:text-slate-600"
              >
                {abierta === una.id ? 'ocultar el rastro' : 'ver el rastro'}
              </button>
            </p>

            {abierta === una.id && <Rastro id={una.id} />}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Par({ etiqueta, valor, mono }: { etiqueta: string; valor: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-slate-400">{etiqueta}</dt>
      <dd className={`font-medium ${mono ? 'font-mono' : ''}`}>{valor}</dd>
    </div>
  )
}

/** Por cuántas manos ha pasado. Era una petición expresa del 15 de octubre. */
function Rastro({ id }: { id: number }) {
  const consulta = useQuery({
    queryKey: ['oportunidad-pases', id],
    queryFn: () => api<{ pases: PaseDeOportunidad[] }>(`/api/oportunidades/${id}/pases`),
  })

  if (consulta.data === undefined) return null

  return (
    <ol className="mt-2 grid gap-1 text-xs text-slate-500">
      {consulta.data.pases.map((pase, indice) => (
        <li key={indice}>
          {cuando(pase.en)} · {pase.accion} {pase.nombre ? `— ${pase.nombre}` : ''}
          {pase.nota ? ` (${pase.nota})` : ''}
        </li>
      ))}
    </ol>
  )
}
